import {
  DeviceCode,
  GroupCode,
  getReferenceId,
  type LocationAcuityCode,
  LocationTypeCode,
  OrganizationCode,
  TemplateTypeCode,
} from '@quromedical/fhir-common';
import {
  type InsuranceOrganization,
  type LocationSearchParams,
  type Medication,
  type MedicationMeasure,
  MedicationRouteCodeDescription,
  type MedicationUse,
  type MessageTemplate,
  type OrganizationSearch,
  type PatientResponse,
  type PersonContactWithAddressEmergencyAccess,
  type PlanDefinition,
  type Specialty,
} from '@quromedical/models';
import type { PathologyTestSearchResult } from '@quromedical/pathology';
import { sortby } from '@quromedical/utils';
import type { PrimitiveValueType, SelectOption } from 'components/types';
import { getDisplayName } from 'core/person';
import type {
  AsyncFetcher,
  Fetcher,
  FetcherSearchOptions,
} from 'design-system';
import { getAddressParts } from 'screens/person';
import { strings } from 'strings';
import { trpcClient } from 'trpc';

import {
  CareTeamApi,
  ChargeItemApi,
  ConsumableApi,
  DeviceApi,
  GooglePlaceApi,
  GroupApi,
  LocationApi,
  MedicalAidApi,
  MedicationApi,
  OrganizationApi,
  PathologyApi,
  PatientApi,
  PractitionerApi,
  SpecialtyApi,
} from './aggregate';
import { ValueSetApi } from './ValueSetApi';

const patientApi = new PatientApi();
const deviceApi = new DeviceApi();
const practitionerApi = new PractitionerApi();
const specialtyApi = new SpecialtyApi();
const groupApi = new GroupApi();
const valueSetApi = new ValueSetApi();
const medicationApi = new MedicationApi();
const googlePlaceApi = new GooglePlaceApi();
const consumableApi = new ConsumableApi();
const organizationApi = new OrganizationApi();
const locationApi = new LocationApi();
const careTeamApi = new CareTeamApi();
const pathologyApi = new PathologyApi();

const hasValue = (value?: string): value is string => !!value;

export const patientFetcher = async (search?: FetcherSearchOptions) => {
  const { patients } = await patientApi.getPatients({ name: search?.query });

  const options = patients
    .map((p) => ({
      value: p.id,
      display: getDisplayName(p.general) || strings.DisplayNotFound,
    }))
    .filter((option) => hasValue(option.display) && hasValue(option.value));

  return options;
};

export const activeDeviceFetcher =
  (type?: DeviceCode): AsyncFetcher<string, undefined> =>
  async (data) => {
    const result = await deviceApi.getDevices({
      status: 'active',
      type,
      'device-name': data?.query,
    });

    return result.devices.map((device) => ({
      value: device.id,
      display: device.name || strings.DisplayNotFound,
    }));
  };

export const relayDeviceFetcher = async (
  search?: FetcherSearchOptions,
): Promise<SelectOption[]> => {
  const result = await deviceApi.getDevices({
    identifier: DeviceCode.relay,
    'device-name': search?.query,
    status: 'active',
  });
  return result.devices.map((device) => ({
    display: device.name || '',
    value: device.id,
  }));
};

export const patchDeviceFetcher = async (
  search?: FetcherSearchOptions,
): Promise<SelectOption[]> => {
  const result = await deviceApi.getDevices({
    identifier: DeviceCode.patch,
    'device-name': search?.query,
    status: 'active',
  });
  return result.devices.map((device) => ({
    display: device.name || '',
    value: device.id,
  }));
};

export const deviceByTypeFetcher =
  (type?: DeviceCode) =>
  async (search?: FetcherSearchOptions): Promise<SelectOption[]> => {
    const result = await deviceApi.getDevices({
      'device-name': search?.query,
      type,
    });

    return result.devices.map((device) => ({
      display: device.name || strings.DisplayNotFound,
      value: device.id,
    }));
  };

export const locationParentFetcher =
  () => async (): Promise<SelectOption[]> => {
    const type = LocationTypeCode.VirtualHospital;
    const status = 'active';

    const searchParams: LocationSearchParams = { status, type };
    const result = await locationApi.getAllLocations(searchParams);

    return result.locations.map((location) => ({
      display: location.name || strings.DisplayNotFound,
      value: location.id,
    }));
  };

type AcuitySelectOption = SelectOption & { acuity?: LocationAcuityCode };

export const locationWardFetcher = async (
  search?: FetcherSearchOptions,
): Promise<AcuitySelectOption[]> => {
  const searchParams: LocationSearchParams = {
    name: search?.query,
    type: LocationTypeCode.VirtualWard,
    status: 'active',
  };
  const result = await locationApi.getAllLocations(searchParams);

  return result.locations.map((location) => ({
    display: location.name || strings.DisplayNotFound,
    value: location.id,
    acuity: location.acuity,
  }));
};

/** *
 * @param type - of type OrganizationCode is used as a filter,
 *  if none is provided the result will be unfiltered
 */
export const organizationFetcher =
  (params?: OrganizationSearch) =>
  async (search?: FetcherSearchOptions): Promise<SelectOption[]> => {
    const result = await organizationApi.getAll({
      ...params,
      name: search?.query,
    });

    const results = result.organizations.map((organization) => ({
      display: organization.general.name || '',
      value: organization.id as string,
    }));
    return results;
  };

export const teamOrDepartmentOrganizationFetcher: AsyncFetcher<
  PrimitiveValueType
> = async (search): Promise<SelectOption[]> => {
  const type = [
    OrganizationCode.OrganizationalTeam,
    OrganizationCode.HospitalDepartment,
  ].join(',');

  const result = await organizationApi.getAll({
    name: search?.query,
    type,
  });

  const results = result.organizations.map((organization) => ({
    display: organization.general.name || '',
    value: organization.id as string,
  }));
  return results;
};

export const practitionerFetcher: AsyncFetcher<PrimitiveValueType> = async (
  search,
): Promise<SelectOption[]> => {
  const result = await practitionerApi.getPractitioners({
    name: search?.query,
  });
  return result.practitioners.map((p) => ({
    display: getDisplayName(p.general) || strings.DisplayNotFound,
    value: p.id,
  }));
};

export const careTeamOrPractitionerFetcher: AsyncFetcher<
  PrimitiveValueType
> = async (search): Promise<SelectOption[]> => {
  const result = await careTeamApi.getAllCareTeams({
    name: search?.query,
    status: 'active',
  });
  const teams = result.careTeams.map((p) => ({
    display: p.name || strings.DisplayNotFound,
    value: p.id,
  }));

  const practitioner = practitionerFetcher(search);
  const [data] = await Promise.all([practitioner]);

  const orderedPractitioners = sortby(data, (practitioner) => practitioner);

  const practitionerOptions = orderedPractitioners.map((practitioner) => ({
    value: practitioner.value as string,
    display: practitioner.display,
  }));

  const merge = teams.concat(practitionerOptions);
  return merge.map((p) => ({
    display: p.display || strings.DisplayNotFound,
    value: p.value,
  }));
};

type ICD10CodeFetcherOptions = FetcherSearchOptions<{
  isChronic?: boolean;
}>;

export const icd10CodeFetcher = async (
  search: ICD10CodeFetcherOptions = {},
): Promise<SelectOption[]> => {
  const { query, isChronic } = search;

  const include = search?.include?.length
    ? {
        include: search.include.join(','),
      }
    : {};

  const options = await valueSetApi.getIcd10Codes({
    search: query,
    isChronic,
    ...include,
  });

  return options;
};

export const chronicIcd10CodeFetcher = async (
  search: FetcherSearchOptions = {},
): Promise<SelectOption[]> => icd10CodeFetcher({ ...search, isChronic: true });

const getMeasureDisplay = (measure?: MedicationMeasure) => {
  if (!(measure?.value && measure?.unit)) {
    return '';
  }
  return `${measure.value} ${measure.unit}`;
};

const getMedicationDisplay = (medication: Medication) => {
  const strength = getMeasureDisplay(medication.strength);
  const routeDescription = MedicationRouteCodeDescription[medication.route];
  return `${medication.name} ${strength} (${routeDescription})`;
};

/** *
 * PresentationCodes and ScheduleCodes are used as filters,
 * if none are provided the result will be unfiltered
 */
export const medicationFetcher =
  (use: MedicationUse) =>
  async (search?: FetcherSearchOptions): Promise<SelectOption[]> => {
    const result = await medicationApi.searchMedication({
      query: search?.query,
      use,
    });

    return result.map((p) => ({
      display: getMedicationDisplay(p),
      value: p.code,
    }));
  };

export type ConsumableFetcher = AsyncFetcher<PrimitiveValueType, any>;
export type CreateConsumableFetcher = () => ConsumableFetcher;
/** *
 * Consumables are filtered by the consumables used by Quro.
 */
export const consumableFetcher: CreateConsumableFetcher =
  () =>
  async (data): Promise<SelectOption[]> => {
    const result = await consumableApi.searchConsumable({
      query: data?.query,
    });

    return result.map((p) => ({
      display: p.name || strings.DisplayNotFound,
      value: p.medpraxCode,
    }));
  };

export const clinicalGroupFetcher = async (): Promise<SelectOption[]> => {
  const result = await groupApi.getGroups({
    code: GroupCode.PractitionerClinical,
  });

  return result.groups.map((p) => ({
    display: p.name || '',
    value: p.id as string,
  }));
};

interface AmpathFetcherOptions {
  commonOnly?: boolean;
  specialOrder?: boolean;
  limit?: number;
}

export const ampathTestFetcher =
  (options: AmpathFetcherOptions = {}) =>
  async (
    data?: FetcherSearchOptions,
  ): Promise<SelectOption<PathologyTestSearchResult>[]> => {
    const { commonOnly = false, specialOrder = false, limit = 100 } = options;
    const query = data?.query;
    const result = await pathologyApi.searchAmpathTests({
      commonOnly,
      specialOrder,
      query,
      limit,
    });

    return result.map((res) => {
      const display = `${res.code} - ${res.description} (${res.department})`;
      return {
        display,
        value: res.code,
        meta: res,
      };
    });
  };

export const specialtyFetcher: AsyncFetcher<string, Specialty> = async () => {
  const result = await specialtyApi.getAll();

  return result.map((specialty) => {
    return {
      display: specialty.name,
      value: specialty.code,
    };
  });
};

export const googleSearchAddressFetcher: AsyncFetcher<string> = async (
  search,
) => {
  const result = await googlePlaceApi.getAddresses(search?.query);

  return result.places.map((p) => ({
    display: p.description || '',
    value: p.placeId,
  }));
};

export const getPlaceAddressPartialFetcher = async (
  placeId: string,
): Promise<Partial<PersonContactWithAddressEmergencyAccess>> => {
  const addressDetails = await googlePlaceApi.getPlaceDetails(placeId);

  const addressComponents = addressDetails.result.addressComponents;

  const contact = getAddressParts(addressComponents);

  return contact;
};

export const organizationSchemeCodeFetcher: AsyncFetcher<
  string,
  InsuranceOrganization
> = async (search) => {
  const orgs = await organizationApi.getInsuranceOrganizations({
    name: search?.query,
  });

  const result = orgs.organizations.map<
    SelectOption<InsuranceOrganization, string>
  >((org) => ({
    display: org.schemeName,
    value: org.schemeCode,
    meta: org,
  }));

  return result;
};

const medicalAidApi = new MedicalAidApi();

export const administratorCodeFetcher: Fetcher<string> = async () => {
  const administratorResponse = await medicalAidApi.getAdministrators();

  return administratorResponse.administrators.map((admin) => ({
    display: admin.administratorName,
    value: admin.administratorCode,
  }));
};

export const schemeCodeFetcher: Fetcher<string> = async () => {
  const schemeResponse = await medicalAidApi.getSchemes();

  return schemeResponse.schemes.map((scheme) => ({
    display: scheme.schemeName,
    value: scheme.schemeCode,
  }));
};

const chargeItemApi = new ChargeItemApi();

export const chargeItemFetcher: AsyncFetcher<string> = async (data) => {
  const result = await chargeItemApi.getChargeItems({
    title: data?.query,
  });

  return result.chargeItemDefinitions.map((item) => ({
    display: item.title || strings.DisplayNotFound,
    value: item.id,
  }));
};

const isDefined = <T>(value?: T): value is T => value !== undefined;

export const ampathCopyDoctorFetcher =
  (patient?: PatientResponse): Fetcher<string> =>
  async () => {
    const practitionerIds =
      patient?.generalPractitioner.practitioners.map(getReferenceId);

    const result = await practitionerApi.getPractitionersByIds(practitionerIds);

    const options = result.practitioners.map((practitioner) => {
      const display =
        getDisplayName(practitioner.general) || strings.DisplayNotFound;
      const value = practitioner.id;
      const ampathMnemonic = practitioner.external?.ampathMnemonic;

      if (!ampathMnemonic) {
        return undefined;
      }
      return {
        display,
        value,
      };
    });

    return options.filter(isDefined);
  };

export const prescriberFetcher =
  (patient?: PatientResponse): Fetcher<string> =>
  async () => {
    const practitionerIds =
      patient?.generalPractitioner.practitioners.map(getReferenceId);

    const result = await practitionerApi.getPractitionersByIds(practitionerIds);

    const practitioners = result.practitioners.filter(
      (practitioner) => practitioner.settings?.canPrescribe,
    );

    const options = practitioners.map((practitioner) => {
      const display =
        getDisplayName(practitioner.general) || strings.DisplayNotFound;
      const value = practitioner.id;
      return {
        display,
        value,
      };
    });

    return options;
  };

export const templateFetchers = async (): Promise<SelectOption[]> => {
  const data = await trpcClient.template.getAll.query({
    type: TemplateTypeCode.TextMessage,
  });

  return data.templates.map((template: MessageTemplate) => ({
    value: template?.content || '',
    display: template?.name || strings.DisplayNotFound,
    meta: template,
  }));
};

export const PlanDefinitionFetcher = async (): Promise<SelectOption[]> => {
  const data = await trpcClient.planDefinition.getAll.query({});
  return data.planDefinitions.map((planDefinition: PlanDefinition) => ({
    value: `PlanDefinition/${planDefinition.id}|${planDefinition.version}`,
    display: planDefinition?.title || strings.DisplayNotFound,
    meta: planDefinition,
  }));
};
